import {
    forwardRef,
    ReactNode,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

export interface StepProps {
    id: string;
    title: string | ReactNode;
    description?: string | ReactNode;
    icon?: ReactNode;
    disabled?: boolean;
    isCompleted?: boolean;
    headerAccessory?: ReactNode;
    children: ReactNode;
}

export const Step = forwardRef<HTMLDivElement, StepProps>(
    (
        {
            id,
            title,
            description,
            icon,
            disabled = false,
            isCompleted = false,
            headerAccessory,
            children,
        },
        ref,
    ) => {
        const textRef = useRef<HTMLDivElement>(null);
        const [squareSize, setSquareSize] = useState<number>(32);

        useLayoutEffect(() => {
            if (textRef.current && icon) {
                const height = textRef.current.offsetHeight;
                setSquareSize(height);
            }
        }, [title, description, icon]);

        return (
            <div className="relative step-connector">
                <AccordionPrimitive.Item
                    ref={ref}
                    value={id}
                    disabled={disabled}
                    className="border border-grey-500 rounded-lg overflow-hidden relative"
                >
                    <AccordionPrimitive.Header className="flex items-center">
                        <AccordionPrimitive.Trigger
                            className={cn(
                                'flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left',
                                'disabled:cursor-not-allowed disabled:opacity-50',
                            )}
                        >
                            {icon && (
                                <span
                                    className="flex-shrink-0 flex items-center justify-center rounded-[4px] text-grey-200 [&>svg]:w-4 [&>svg]:h-4 [&>*]:w-4 [&>*]:h-4"
                                    style={{
                                        width: `${squareSize}px`,
                                        height: `${squareSize}px`,
                                        background: 'var(--grey-500)',
                                    }}
                                >
                                    {icon}
                                </span>
                            )}
                            <div
                                ref={textRef}
                                className="flex-1 flex flex-col gap-0.5"
                            >
                                <span className="text-sm-medium text-white-100">
                                    {title}
                                </span>
                                {description && (
                                    <span className="text-sm text-grey-200">
                                        {description}
                                    </span>
                                )}
                            </div>
                            {isCompleted && (
                                <CheckCircleIcon className="w-5 h-5 text-green-bright flex-shrink-0" />
                            )}
                        </AccordionPrimitive.Trigger>
                        {headerAccessory ? (
                            <div className="shrink-0 pr-4">
                                {headerAccessory}
                            </div>
                        ) : null}
                    </AccordionPrimitive.Header>
                    <AccordionPrimitive.Content
                        className={cn(
                            'overflow-hidden transition-all',
                            'data-[state=closed]:animate-accordion-up',
                            'data-[state=open]:animate-accordion-down',
                        )}
                    >
                        <div className="px-4 py-4">{children}</div>
                    </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
            </div>
        );
    },
);

Step.displayName = 'Step';
